// @flow
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { ClientIdContext, mapDataWithIdAndNameToOptions } from 'bk-web-app';
import { JournalEntrySidebar } from 'journal-entry-sidebar';
import { NullState } from 'bench-internal-theme';
import React from 'react';
// $FlowFixMe - TS file
import { SelectedDataEntriesProvider } from 'general-ledger';
import { WrappedSpinner } from 'spinner';

import CategorizationView from './CategorizationView';
import SequentialQueries from './SequentialQueries';
import { GET_CATEGORIZATION_INCONSISTENCIES, GET_TRANSACTION_CATEGORIZATIONS } from './queries';
import type { DateRange } from './types';
import compileCategorizationViewData from './compileCategorizationViewData';
import makeClientBooksQueries from './makeClientBooksQueries';
import mergeDatasets from './mergeDatasets';

type CategorizationViewContainerProps = {
  dateRange: DateRange,
  search: string,
};

const CategorizationViewContainer = ({ dateRange, search }: CategorizationViewContainerProps) => (
  <ClientIdContext.Consumer>
    {clientId => (
      <SequentialQueries
        queries={makeClientBooksQueries({
          dateRange,
          clientId,
          supplementalQueriesByMonth: [GET_TRANSACTION_CATEGORIZATIONS],
          supplementalQueriesForRange: [GET_CATEGORIZATION_INCONSISTENCIES],
        })}
      >
        {getClientBooks => {
          if (getClientBooks.loading) {
            return <WrappedSpinner />;
          }
          if (getClientBooks.error || !getClientBooks.data) {
            return (
              <NullState
                bigEmoji="🙈"
                description="Uh oh! There seems to be a problem, sorry!"
                title="There was a problem loading data for the Categorization Tab."
              />
            );
          }
          const {
            addOns,
            categorizationInconsistencies,
            dataEntries,
            journalEntries,
            ledgerAccounts,
            transactionCategorizations,
          } = mergeDatasets(getClientBooks.data);
          const transactionCategorizationsList = transactionCategorizations || [];
          const { awaitingCategoryExpense, awaitingCategoryRevenue } =
            compileCategorizationViewData(
              addOns,
              categorizationInconsistencies,
              dataEntries,
              journalEntries,
              ledgerAccounts,
              transactionCategorizationsList,
            );
          if (
            awaitingCategoryExpense.dataEntries.length === 0 &&
            awaitingCategoryRevenue.dataEntries.length === 0
          ) {
            return (
              <NullState
                bigEmoji="🤩"
                description="Great job keeping tidy books."
                title="You have no transactions awaiting categorization!"
              />
            );
          }
          return (
            <SelectedDataEntriesProvider>
              <AppSidebar>
                {({ closeSidebar, openSidebar, openerMeta }) => {
                  const selectedDataEntry =
                    // eslint-disable-next-line no-underscore-dangle
                    openerMeta && openerMeta.__typename === 'DataEntry' ? openerMeta : undefined;
                  const toggleDataEntry = dataEntry => {
                    if (dataEntry && dataEntry !== openerMeta) {
                      openSidebar(
                        <JournalEntrySidebar
                          dismiss={closeSidebar}
                          journalEntryId={dataEntry.journalEntry.id}
                          key={dataEntry.journalEntry.id}
                        />,
                        dataEntry,
                      );
                    } else {
                      closeSidebar();
                    }
                  };
                  return (
                    <CategorizationView
                      awaitingCategoryExpense={awaitingCategoryExpense}
                      awaitingCategoryRevenue={awaitingCategoryRevenue}
                      ledgerOptions={mapDataWithIdAndNameToOptions(ledgerAccounts)}
                      search={search}
                      selectedDataEntry={selectedDataEntry}
                      toggleDataEntry={toggleDataEntry}
                    />
                  );
                }}
              </AppSidebar>
            </SelectedDataEntriesProvider>
          );
        }}
      </SequentialQueries>
    )}
  </ClientIdContext.Consumer>
);
export default CategorizationViewContainer;
